"use client";

import * as React from "react";
import { AppWindow, Bot, KeyRound, Plug } from "lucide-react";

import { cn, paletteIndex, type IdentityKind } from "../utils";
import { UserIcon } from "./user-icon";

type AvatarSize = "xs" | "sm" | "md";

const BOX_SIZE: Record<AvatarSize, string> = { xs: "h-5 w-5", sm: "h-6 w-6", md: "h-8 w-8" };
const GLYPH_SIZE: Record<AvatarSize, string> = { xs: "h-3 w-3", sm: "h-3.5 w-3.5", md: "h-4 w-4" };

// Listed in full so Tailwind generates each class; order must stay stable or
// every agent changes colour.
const AGENT_COLORS = [
  "bg-agent-1",
  "bg-agent-2",
  "bg-agent-3",
  "bg-agent-4",
  "bg-agent-5",
  "bg-agent-6",
  "bg-agent-7",
  "bg-agent-8",
  "bg-agent-9",
  "bg-agent-10",
] as const;

/** Which agent palette slot (0-based) a stable key lands on; chips share it. */
export function agentColorIndex(key: string): number {
  return paletteIndex(key, AGENT_COLORS.length);
}

function AgentAvatar({ colorKey, size = "xs" }: { colorKey: string; size?: AvatarSize }) {
  return (
    <span
      className={cn(
        "flex shrink-0 items-center justify-center rounded-md text-agent-foreground",
        AGENT_COLORS[agentColorIndex(colorKey)],
        BOX_SIZE[size],
      )}
      aria-hidden
    >
      <Bot className={GLYPH_SIZE[size]} />
    </span>
  );
}

/**
 * Non-human actors that are not agents: an API key, an OAuth client, or a
 * custom app. Icon-in-a-square like the agent avatar, on the muted surface so
 * credentials and apps read as "something ran", never as a person.
 */
const NON_HUMAN_ICONS: Partial<Record<IdentityKind, React.ComponentType<{ className?: string }>>> =
  {
    api_key: KeyRound,
    oauth_client: Plug,
    custom_app: AppWindow,
  };

function NonHumanAvatar({ kind, size = "xs" }: { kind: IdentityKind; size?: AvatarSize }) {
  const Icon = NON_HUMAN_ICONS[kind] ?? Bot;
  return (
    <span
      className={cn(
        "flex shrink-0 items-center justify-center rounded-md bg-muted text-muted-foreground",
        BOX_SIZE[size],
      )}
      aria-hidden
    >
      <Icon className={GLYPH_SIZE[size]} />
    </span>
  );
}

export interface IdentityAvatarProps {
  kind?: IdentityKind | null | undefined;
  /** Stable id (e.g. `agt_…`) that picks an agent's colour; falls back to `name`. */
  id?: string | null | undefined;
  /** Resolved display label; users derive their initials from it. */
  name: string;
  email?: string | null | undefined;
  avatarUrl?: string | null | undefined;
  size?: AvatarSize | undefined;
}

/**
 * The avatar for any actor: a rounded square for agents and other non-human
 * kinds, a round photo or initials for people. Use this wherever an identity is
 * listed so a kind always looks the same across the app.
 */
export function IdentityAvatar({
  kind,
  id,
  name,
  email,
  avatarUrl,
  size = "xs",
}: IdentityAvatarProps): React.JSX.Element {
  if (kind === "agent") return <AgentAvatar colorKey={id || name} size={size} />;
  const nonHumanKind = kind && kind in NON_HUMAN_ICONS ? kind : null;
  if (nonHumanKind) return <NonHumanAvatar kind={nonHumanKind} size={size} />;
  return <UserIcon name={name} email={email ?? name} avatarUrl={avatarUrl} size={size} />;
}
